import { Button, SideNavigation } from '@robocorp/components';
import { IconCloseSmall } from '@robocorp/icons';
import { Workspace } from '@robocorp/robocloud-internal-cds-sdk';
import { notNil } from '@robocorp/robocloud-shared-utils';
import { MouseEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigationLink } from '~/components';
import { LocalStorageItemKeys, useActions, useIsSSO, useLocalStorage, useSelector } from '~/hooks';

export const WelcomeLink = ({ workspace }: { workspace: Workspace }) => {
  const { t } = useTranslation();
  const { isSSO } = useIsSSO();

  const { storageValue: welcomeMessageDismiss, setStorageValue: persistWelcomeMessageDismiss } =
    useLocalStorage<string | undefined>({
      key: LocalStorageItemKeys.HAS_DISMISSED_WELCOME_MESSAGE,
      initialValue: undefined,
      sync: true,
    });
  const hasDismissedWelcomeMessage = notNil(welcomeMessageDismiss);

  const gettingStartedDismissedTimestamp = useSelector(
    (state) => state.userResources.users.current?.details?.getting_started_dismissed_ts,
  );
  const { dismissGettingStarted } = useActions(({ userActions }) => ({
    dismissGettingStarted: userActions.dismissGettingStarted,
  }));

  const onClose = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const currentTimestamp = new Date().toISOString();
      if (isSSO) {
        return persistWelcomeMessageDismiss(currentTimestamp);
      }
      return dismissGettingStarted(currentTimestamp);
    },
    [dismissGettingStarted, persistWelcomeMessageDismiss, isSSO],
  );

  const hasGettingStartedDismissedTimestamp = typeof gettingStartedDismissedTimestamp === 'string';
  const hasProcesses = (workspace?.process_count ?? 0) > 0;
  if (hasGettingStartedDismissedTimestamp || hasDismissedWelcomeMessage || hasProcesses) {
    return null;
  }

  return (
    <SideNavigation.ItemGroup>
      <NavigationLink component={SideNavigation.Link} to="/welcome" icon="👋">
        {t('onboarding.gettingStarted')}
      </NavigationLink>
      <Button
        size="small"
        variant="ghost"
        icon={IconCloseSmall}
        aria-label="Close"
        onClick={onClose}
      />
    </SideNavigation.ItemGroup>
  );
};
